import React, { useEffect, useState, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { formatDateTime } from "../utils/dateUtils";
import RFQTimeline from "./RFQTimeline";

function capitalize(str) {
  if (!str) return "";
  // Replace dashes with spaces, then capitalize
  return str.replace(/-/g, ' ').charAt(0).toUpperCase() + str.replace(/-/g, ' ').slice(1);
}

function ArchiveItem({ item, onClick }) {
  const stateColors = {
    "New Request": "bg-blue-100 text-blue-800",
    "Quoted": "bg-purple-100 text-purple-800",
    "Countered": "bg-yellow-100 text-yellow-800",
    "Accepted": "bg-green-100 text-green-800",
    "Awarded": "bg-teal-100 text-teal-800",
    "Rejected": "bg-red-100 text-red-800",
  };
  const stateClass = stateColors[item.state] || "bg-gray-100 text-gray-800";

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-3 shadow-none flex flex-col gap-2 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onClick(item)}
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate" title={item.rfqNumber}>
          RFQ #{item.rfqNumber}
        </span>
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${stateClass}`}>{item.state}</span>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDateTime(item.time)}</div>

      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Category:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.subMaterial}>
            {capitalize(item.subMaterial)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Material:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.material}>
            {capitalize(item.material)}{item.subMaterial ? ` (${capitalize(item.subMaterial)})` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Dealer:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.dealer}>
            {item.dealer}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Supplier:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.supplier}>
            {item.supplier}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Qty:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={`${item.quantity}`}>
            {item.quantity}
          </span>
        </div>
        {item.quote && item.quote.price !== null && item.quote.price !== undefined && (
          <div className="flex items-center gap-2">
            <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Quote Price:</span>
            <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 font-semibold truncate" title={`$${item.quote.price.toLocaleString()}`}>
              ${typeof item.quote.price === 'number' ? item.quote.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : item.quote.price}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RFQArchive({ clientId, onOpenChat }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  // Range in minutes for archive fetch; 0 means "All". Default = 30 days
  const [rangeMinutes, setRangeMinutes] = useState(60 * 24 * 30);
  const [filterStates, setFilterStates] = useState([]);
  const [filterDealers, setFilterDealers] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterMaterials, setFilterMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const filterContainerRef = React.useRef(null);

  // Extract loadArchive as a function that can be called on demand
  const loadArchive = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      // If rangeMinutes === 0, request all history (no minutes param). Otherwise use rangeMinutes directly.
      let url = getApiUrl(`/api/history/events/client/${encodeURIComponent(clientId)}`);
        if (rangeMinutes && Number(rangeMinutes) > 0) {
          const minutes = Number(rangeMinutes);
          url = getApiUrl(`/api/history/events/client/${encodeURIComponent(clientId)}?minutes=${minutes}`);
        }
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch archive: ${res.status}`);
        const data = await res.json();
        
        const rawArr = Array.isArray(data) ? data : data?.history ?? data?.items ?? data?.newHistory ?? [];
        
        const mapped = rawArr.map((historyItem) => {
          const rfq = historyItem.rfq || {};
          const quote = historyItem.quote || null;
          return {
            id: historyItem.id || `archive-${Date.now()}-${Math.random()}`,
            rfqNumber: rfq.rfqNumber || historyItem.rfqNumber,
            dealer: historyItem.dealerName || historyItem.dealer?.name || "Unknown dealer",
            supplier: historyItem.supplierName || historyItem.supplier?.name || historyItem.dealer?.supplier?.name || historyItem.dealerCompany || "null",
            state: rfq.location?.state || rfq.state || historyItem.location?.state || historyItem.state || "N/A",
            quantity: rfq.quantity ?? historyItem.quantity ?? -1,
            material: rfq.material || historyItem.material || "N/A",
            subMaterial: rfq.subtype || historyItem.subtype || "",
            materialCategory: rfq.materialCategory || historyItem.materialCategory || "",
            status: rfq.status || historyItem.status,
            time: historyItem.createdAt || new Date().toISOString(),
            quote: quote ? {
              id: quote.id,
              price: quote.price,
              deliveryTime: quote.deliveryTime,
              terms: quote.terms,
              notes: quote.notes,
              validUntil: quote.validUntil,
              createdAt: quote.createdAt,
              updatedAt: quote.updatedAt,
            } : null,
          };
        });
        
        // Deduplicate by rfqNumber, keeping only the most recent (first occurrence after sorting by time)
        const deduped = [];
        const seenRfqNumbers = new Set();
        
        // Sort by time descending (newest first)
        const sorted = mapped.sort((a, b) => new Date(b.time) - new Date(a.time));
        
        for (const item of sorted) {
          if (item.rfqNumber) {
            if (!seenRfqNumbers.has(item.rfqNumber)) {
              seenRfqNumbers.add(item.rfqNumber);
              deduped.push(item);
            }
          } else {
            // Items without rfqNumber are always included
            deduped.push(item);
          }
        }
        
        console.log('[RFQArchive] Loaded:', mapped.length, 'items, deduplicated to:', deduped.length);
        setItems(deduped);
      } catch (e) {
        console.warn("[RFQArchive] Failed to load archive:", e);
      } finally {
        setLoading(false);
      }
    }, [clientId, rangeMinutes]);

  useEffect(() => {
    loadArchive();
  }, [clientId, rangeMinutes, loadArchive]);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterContainerRef.current && !filterContainerRef.current.contains(event.target)) {
        // Close all open details elements
        const openDetails = filterContainerRef.current.querySelectorAll('details[open]');
        openDetails.forEach(details => {
          details.removeAttribute('open');
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleItemClick = (item) => {
    setSelectedRFQ(item);
    setShowTimeline(true);
  };

  const handleCloseTimeline = () => {
    setShowTimeline(false);
    setSelectedRFQ(null);
  };

  // Build filter options from items
  const allStates = Array.from(new Set(items.map(it => it.state).filter(Boolean)));
  const allDealers = Array.from(new Set(items.map(it => it.dealer).filter(Boolean)));
  const allCategories = Array.from(new Set(items.map(it => it.subMaterial).filter(Boolean)));
  const allMaterials = Array.from(new Set(items.map(it => it.material).filter(Boolean)));

  // Toggle filter selection
  const toggleFilter = (setFilter, value) => {
    setFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // Apply all filters (multi-select OR logic within each category, AND logic across categories)
  const filteredItems = items.filter(it => {
    const stateMatch = filterStates.length === 0 || filterStates.includes(it.state);
    const dealerMatch = filterDealers.length === 0 || filterDealers.includes(it.dealer);
    const categoryMatch = filterCategories.length === 0 || filterCategories.includes(it.subMaterial);
    const materialMatch = filterMaterials.length === 0 || filterMaterials.includes(it.material);
    const searchMatch = !searchQuery || 
      (it.rfqNumber && it.rfqNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (it.dealer && it.dealer.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (it.material && it.material.toLowerCase().includes(searchQuery.toLowerCase()));
    return stateMatch && dealerMatch && categoryMatch && materialMatch && searchMatch;
  });

  // Active filters to display
  const activeFilters = [
    ...filterStates.map(v => ({ label: "State", value: v })),
    ...filterDealers.map(v => ({ label: "Dealer", value: v })),
    ...filterCategories.map(v => ({ label: "Category", value: v, cap: true })),
    ...filterMaterials.map(v => ({ label: "Material", value: v, cap: true })),
  ];

  // Clear all filters
  const clearFilters = () => {
    setFilterStates([]);
    setFilterDealers([]);
    setFilterCategories([]);
    setFilterMaterials([]);
  };

  return (
    <>
      <div className="flex-1 p-4 overflow-y-auto bg-white dark:bg-gray-900">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">RFQ Archive</h2>
          
          {/* Search input */}
          <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by RFQ #, dealer, material..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date/time range selector */}
            <div className="mt-2 sm:mt-0">
              <label className="text-xs text-gray-600 dark:text-gray-400 mr-2">Range:</label>
              <select
                value={rangeMinutes}
                onChange={(e) => setRangeMinutes(Number(e.target.value))}
                className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                aria-label="Archive date range"
                title="Select how far back to fetch archived RFQs"
              >
                <option value={60}>Last 1 hour</option>
                <option value={240}>Last 4 hours</option>
                <option value={720}>Last 12 hours</option>
                <option value={1440}>Last 24 hours</option>
                <option value={10080}>7 days</option>
                <option value={43200}>30 days</option>
                <option value={129600}>90 days</option>
                <option value={0}>All</option>
              </select>
            </div>
          </div>
          
          {/* Multi-select filter row */}
          <div className="mb-4" style={{ background: "transparent" }} ref={filterContainerRef}>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
              
              <div className="flex flex-wrap gap-2 items-start">
                {/* State - Multi-select with checkboxes */}
                <details className="group">
                  <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                    filterStates.length > 0
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700"
                      : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                  } hover:bg-gray-50 dark:hover:bg-gray-600`}>
                    State {filterStates.length > 0 && `(${filterStates.length})`}
                  </summary>
                  <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto z-10 absolute">
                    {allStates.map(state => (
                      <label key={state} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterStates.includes(state)}
                          onChange={() => toggleFilter(setFilterStates, state)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{state}</span>
                      </label>
                    ))}
                  </div>
                </details>

                {/* Dealer - Multi-select with checkboxes */}
                <details className="group">
                  <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                    filterDealers.length > 0
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700"
                      : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                  } hover:bg-gray-50 dark:hover:bg-gray-600`}>
                    Dealer {filterDealers.length > 0 && `(${filterDealers.length})`}
                  </summary>
                  <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto z-10 absolute">
                    {allDealers.map(dealer => (
                      <label key={dealer} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterDealers.includes(dealer)}
                          onChange={() => toggleFilter(setFilterDealers, dealer)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{dealer}</span>
                      </label>
                    ))}
                  </div>
                </details>

                {/* Category - Multi-select with checkboxes */}
                <details className="group">
                  <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                    filterCategories.length > 0
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700"
                      : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                  } hover:bg-gray-50 dark:hover:bg-gray-600`}>
                    Category {filterCategories.length > 0 && `(${filterCategories.length})`}
                  </summary>
                  <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto z-10 absolute">
                    {allCategories.map(cat => (
                      <label key={cat} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterCategories.includes(cat)}
                          onChange={() => toggleFilter(setFilterCategories, cat)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{capitalize(cat)}</span>
                      </label>
                    ))}
                  </div>
                </details>

                {/* Material - Multi-select with checkboxes */}
                <details className="group">
                  <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                    filterMaterials.length > 0
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700"
                      : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                  } hover:bg-gray-50 dark:hover:bg-gray-600`}>
                    Material {filterMaterials.length > 0 && `(${filterMaterials.length})`}
                  </summary>
                  <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto z-10 absolute">
                    {allMaterials.map(mat => (
                      <label key={mat} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterMaterials.includes(mat)}
                          onChange={() => toggleFilter(setFilterMaterials, mat)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{capitalize(mat)}</span>
                      </label>
                    ))}
                  </div>
                </details>

                {/* Clear filters button */}
                <button
                  type="button"
                  onClick={clearFilters}
                  disabled={activeFilters.length === 0}
                  className="text-xs border rounded-sm px-3 py-1.5 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 shadow-none hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed h-[34px]"
                  aria-label="Clear all filters"
                  title="Clear all filters"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Active filters indicator */}
          <div className="mb-4">
            {activeFilters.length > 0 ? (
              <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                <span className="text-xs text-gray-600 dark:text-gray-400 shrink-0">Active:</span>
                <div className="flex items-center gap-1 overflow-hidden flex-wrap">
                  {activeFilters.map((f) => (
                    <span
                      key={`${f.label}-${f.value}`}
                      className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-sm px-2 py-0.5 truncate max-w-[8rem]"
                      title={`${f.label}: ${f.cap ? capitalize(f.value) : f.value}`}
                    >
                      {f.label}: {f.cap ? capitalize(f.value) : f.value}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">No filters applied</div>
            )}
          </div>
        </div>
        
        {loading && (
          <div className="text-gray-600 dark:text-gray-400">Loading archive...</div>
        )}
        
        {!loading && filteredItems.length === 0 && items.length > 0 && (
          <div className="text-gray-600 dark:text-gray-400">No RFQs match the current filters.</div>
        )}
        
        {!loading && items.length === 0 && (
          <div className="text-gray-600 dark:text-gray-400">No archived RFQs found.</div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <ArchiveItem key={item.id} item={item} onClick={handleItemClick} />
          ))}
        </div>
      </div>

      {showTimeline && selectedRFQ && (
        <RFQTimeline 
          rfqNumber={selectedRFQ.rfqNumber} 
          onClose={handleCloseTimeline}
          onOpenChat={onOpenChat}
          clientId={clientId}
          onUpdate={loadArchive}
        />
      )}
    </>
  );
}
